import { getAreaName } from '../lib/area';
import type { Issue, Me } from '@ft/shared';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loading, PmChips, useLoad } from '../components/ui';
import { useI18n, type T } from '../i18n';
import { dfmt, eur, pct } from '../lib/format';
import { Chip, CollapseAll, Panel } from '../lib/collapse';

const TONE = { neg: 'neg', crit: 'crit', info: 'neu' } as const;

/** Text eines offenen Punkts in der gewählten Sprache. Beträge kommen in Cent. */
export function issueText(i: Issue, t: T): { title: string; detail: string } {
  const p = i.params;
  const n = (k: string) => Number(p[k] ?? 0);
  const order = `${p.order || t('common.noNumber')}${p.warranty ? t('issues.warrantySuffix') : ''}`;
  const vals = {
    ...p, order,
    billed: eur(n('billed')), volume: eur(n('volume')), rest: eur(n('volume') - n('billed')),
    ordered: eur(n('ordered')), budget: eur(n('budget')), over: eur(n('ordered') - n('budget')),
    missing: [p.link ? '' : t('ct.missingLink'), p.pdf ? '' : t('ct.missingPdf')].filter(Boolean).join(t('ct.and')),
    pct: n('volume') ? pct(n('billed') / n('volume')) : '–',
    date: p.date ? dfmt(String(p.date)) : '',
    due: eur(n('due')),
  };
  return { title: t(`issue.${i.code}.t`, vals), detail: t(`issue.${i.code}.d`, vals) };
}

/** Offene Punkte werden laufend aus den Daten berechnet und verschwinden, sobald sie behoben sind. */
export function IssuesPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const { data, error } = useLoad<Issue[]>('/api/issues');
  const [mine, setMine] = useState(false);
  const list = (data ?? []).filter((i) => !mine || i.pms.some((p) => p.id === me.id));

  return (
    <section className="page" style={{ maxWidth: 960 }}>
      <div className="pagehead">
        <div><div className="crumb">{t('crumb.root', { area: getAreaName() })}</div><h1>{t('issues.title')}</h1></div>
        <span className="spacer" />
        <CollapseAll ids={['issues.neg', 'issues.crit', 'issues.info'].filter((id) => list.some((i) => `issues.${i.level}` === id))} />
        <div className="seg" role="group" aria-label={t('common.filter')}>
          <button aria-pressed={!mine} onClick={() => setMine(false)}>{t('common.all')}</button>
          <button aria-pressed={mine} onClick={() => setMine(true)}>{t('common.myProjects')}</button>
        </div>
      </div>
      <p className="muted lead">{t('issues.lead')}</p>
      {!data ? <Loading error={error} /> : !list.length ? <div className="box"><div className="empty">{t('issues.empty')}</div></div> : (
        (['neg', 'crit', 'info'] as const).map((lvl) => {
          const xs = list.filter((i) => i.level === lvl);
          if (!xs.length) return null;
          return (
            <Panel key={lvl} id={`issues.${lvl}`} title={`${t(`issues.level.${lvl}`)} (${xs.length})`}
              summary={<Chip tone={lvl === 'info' ? undefined : lvl}><b>{xs.length}</b> · {issueText(xs[0]!, t).title}{xs.length > 1 ? ' …' : ''}</Chip>}>
              <ul className="issues">
                {xs.map((i) => {
                  const x = issueText(i, t);
                  return (
                    <li key={i.key}>
                      <span><span className={`pill ${TONE[i.level]}`}>{t(`issues.level.${i.level}`)}</span></span>
                      <div><div className="t">{x.title}</div><div className="muted small">{x.detail}</div></div>
                      <div className="row" style={{ flexWrap: 'nowrap' }}>{i.pms.length > 0 && <PmChips pms={i.pms} />}{(me.role === 'admin' || !i.link.startsWith('/verwaltung')) && <Link className="btn" to={i.link}>{t('common.open')}</Link>}</div>
                    </li>
                  );
                })}
              </ul>
            </Panel>
          );
        })
      )}
    </section>
  );
}
